/**
 * 身分作用域 store：記錄「哪個 token 屬於哪個帳號」與「哪個 receiveNo 是用哪個 token 取得的」，
 * 並在換帳號 / 登出時重置所有提供 resetUserScoped() 的 store。
 * 契約見 spec/2026-10-19-reward-claim-identity-scope/design.md §5.2～§5.4。
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

import { isNonEmptyString } from '@/utils/identityGuard'

// 遠大於一個頁面壽命內會出現的筆數（獎勵清單每輪 ≤ 90 筆、下線清單每頁 15 列）；被淘汰的會被擋成 not-admitted（fail-closed）
const MAX_ADMISSIONS = 2000
// 一個頁面壽命內通常只有個位數的 token：extendToken 不換值，背景 ≥ 300 秒由 L3 重載、前景失效走登出
const MAX_TOKEN_OWNERS = 50

export const useIdentityScopeStore = defineStore('identityScope', () => {
  // 目前已確認的帳號（供 L3 比對退背景前後是否換了帳號）
  const uidSite = ref(null)

  // 歸屬紀錄刻意不放進 state：不進 SSR payload、不進 devtools 序列化，只存在 client 記憶體（BR-5）。
  // key 一律是 identityKey() 的雜湊，不是原始 token（BR-4）
  const tokenOwners = new Map() // tokenKey → uid_site
  const admissions = new Map() // receiveNo → 取得它時的 tokenKey
  const userScopedStores = new Set()

  function registerUserScopedStore(store) {
    userScopedStores.add(store)
  }

  function resetUserScopedStores() {
    userScopedStores.forEach((store) => store.resetUserScoped())
  }

  // 先刪再設：每次重新登記都移到最後，淘汰的是最久沒被任何資料來源重新登記的那筆
  function setAdmission(receiveNo, tokenKey) {
    admissions.delete(receiveNo)
    admissions.set(receiveNo, tokenKey)
    if (admissions.size > MAX_ADMISSIONS) admissions.delete(admissions.keys().next().value)
  }

  // 只刪沒有 receiveNo 指向的 token：刪掉仍被指向的，那些 receiveNo 會因擁有者不明被誤擋成 foreign。
  // admissions 有上限，仍被指向的 token 數也就有上限
  function pruneUnreferencedTokenOwners() {
    const referencedTokenKeys = new Set(admissions.values())
    tokenOwners.forEach((_, tokenKey) => {
      if (!referencedTokenKeys.has(tokenKey)) tokenOwners.delete(tokenKey)
    })
  }

  // /self 成功且飛行期間 token 未變時呼叫（REQ-2）
  function confirm({ tokenKey, uidSite: nextUidSite }) {
    if (!isNonEmptyString(tokenKey) || !isNonEmptyString(nextUidSite)) return

    if (!tokenOwners.has(tokenKey) && tokenOwners.size >= MAX_TOKEN_OWNERS) pruneUnreferencedTokenOwners()
    tokenOwners.set(tokenKey, nextUidSite)

    // 由空變有值是首次登入：清單可能比 /self 先回、已登記在新 token 下，不能清（design §5.3）
    if (uidSite.value && uidSite.value !== nextUidSite) {
      resetUserScopedStores()
    }
    uidSite.value = nextUidSite
  }

  // 登出（REQ-5）
  function end() {
    tokenOwners.clear()
    admissions.clear()
    resetUserScopedStores()
    uidSite.value = null
  }

  // 資料來源取得 receiveNo 後呼叫；tokenKey 必須是「請求發出當下」的（BR-1, REQ-6）
  function admit(tokenKey, receiveNos) {
    if (!isNonEmptyString(tokenKey)) return
    receiveNos.forEach((receiveNo) => {
      if (receiveNo === undefined || receiveNo === null || receiveNo === '' || receiveNo === 0) return
      setAdmission(String(receiveNo), tokenKey)
    })
  }

  const ownerOf = (tokenKey) => (isNonEmptyString(tokenKey) ? (tokenOwners.get(tokenKey) ?? null) : null)

  const admissionOf = (receiveNo) => admissions.get(String(receiveNo))

  return {
    uidSite,
    registerUserScopedStore,
    confirm,
    end,
    admit,
    ownerOf,
    admissionOf,
  }
})
